import { useRef, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';
import { useFretsStore, getSelectedInstrument } from '../store/useFretsStore';
import { ACTIONS, TICKS_PER_BEAT_BINARY, TICKS_PER_BEAT_TERNARY, SECONDS_IN_MINUTE, SCHEDULE_AHEAD_TIME } from '../config/frets.config';
import { getMidiPitch } from '../utils/pitch.utils';
import { playPluckedNote } from '../utils/audio.utils';
const { START, STOP, UPDATE, TICK } = ACTIONS.TIMER;
const EMPTY_ARRAY = Object.freeze([]);

const worker = new Worker(new URL('../workers/timer.worker.js', import.meta.url), { type: 'module' });

const useMetronome = ({ tempo = 120, subdivision = 1 } = {}) => {
    const { exercise } = useParams();
    const running = useFretsStore(state => state.sequencePlaying);
    const setRunning = useFretsStore(state => state.setSequencePlaying);
    const selectedInstrumentId = useFretsStore(state => state.selectedInstrumentId);
    const activeInstrumentDefinition = useFretsStore(state => state.activeInstrumentDefinition);
    const instrument = getSelectedInstrument(activeInstrumentDefinition || selectedInstrumentId);
    const [ state, setState ] = useState({
        beat: 0,
        subBeat: 0,
        tempo,
        subdivision,
    });
    const [ beatsPerMeasure, setBeatsPerMeasure ] = useState(instrument.strings.length);
    const [ beat, setBeat ] = useState(0);
    // Each quarter-note beat has a fixed number of scheduler ticks. A measure
    // contains one beat per string for these exercises, so its total tick count
    // must scale with string count (including five-string banjos).
    const ticksPerBeat = beatsPerMeasure % 3 === 0 || state.subdivision % 3 === 0 ? TICKS_PER_BEAT_TERNARY : TICKS_PER_BEAT_BINARY;
    const ticksPerMeasure = ticksPerBeat * beatsPerMeasure;
    const audioContext = useRef(null);
    const nextNoteTime = useRef(0);
    const currentBeat = useRef(0);
    const setExerciseBeat = useFretsStore(state => state.setExerciseBeat);
    const pattern = useFretsStore(state => state.exercises[String(exercise ?? '')]?.pattern ?? EMPTY_ARRAY);
    const exerciseStep = useRef(0);

    const runScheduler = () => {
        const context = audioContext.current;
        if (!context || context.state === 'closed') return;
        while (nextNoteTime.current < context.currentTime + SCHEDULE_AHEAD_TIME) {
            const scheduledTime = nextNoteTime.current;
            const loopCompleted = tick(currentBeat.current, scheduledTime);
            const secondsPerBeat = SECONDS_IN_MINUTE / state.tempo;
            const beatInterval = secondsPerBeat / ticksPerBeat;
            nextNoteTime.current = scheduledTime + beatInterval;

            // Make the loop transition relative to the selected tempo instead
            // of using a fixed delay: resume at 75% of the normal note interval.
            // The plucked note fades toward silence before its envelope ends.
            if (loopCompleted)
                nextNoteTime.current = scheduledTime + beatInterval * 0.75;

            currentBeat.current = currentBeat.current + 1;
            if (currentBeat.current === ticksPerMeasure)
                currentBeat.current = 0;
        }
    };

    const tick = (beat, time) => {
        const isFirstBeat = beat === 0;
        const isQuarterBeat = beat % ticksPerBeat === 0;

        // Exercise notes are the audible beat; do not layer the old click track over them.
        if (isFirstBeat || isQuarterBeat) {
            let loopCompleted = false;
            let scheduledExerciseIndex = null;
            if (exercise && pattern.length) {
                scheduledExerciseIndex = exerciseStep.current % pattern.length;
                const position = pattern[scheduledExerciseIndex];
                if (position) {
                    try {
                        playPluckedNote(getMidiPitch(instrument, position.string, position.fret), {
                            context: audioContext.current,
                            time,
                            duration: 0.42,
                            volume: 0.22,
                        });
                    } catch (error) {
                        // Keep the scheduler running if note playback is unavailable.
                    }
                }
                exerciseStep.current = (exerciseStep.current + 1) % pattern.length;
                loopCompleted = exerciseStep.current === 0;
            }

            // Publish the exact exercise position when it is scheduled. The
            // shared store is the single source of truth for both the fretboard
            // and tablature; updating it here prevents the final position from
            // being lost if a playback-time callback races with a loop boundary.
            if (scheduledExerciseIndex !== null)
                setExerciseBeat(exercise, scheduledExerciseIndex + 1);

            // Keep the metronome's own beat counter aligned to the audible time.
            const context = audioContext.current;
            const delay = context ? Math.max(0, (time - context.currentTime) * 1000) : 0;
            setTimeout(() => {
                setState(prevState => ({
                    ...prevState,
                    beat: prevState.beat === beatsPerMeasure ? 1 : prevState.beat + 1 || 1,
                }));
                const nextExerciseBeat = scheduledExerciseIndex === null
                    ? null
                    : scheduledExerciseIndex + 1;
                setBeat(prevBeat => nextExerciseBeat === null
                    ? prevBeat + 1
                    : nextExerciseBeat);
            }, delay);
            return loopCompleted;
        }

        return false;
    };

    const start = () => {
        if (!audioContext.current) {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) audioContext.current = new AudioContextClass();
        }
        if (!audioContext.current) return;
        if (audioContext.current.state === 'suspended') audioContext.current.resume();

        // Do not restart the audio timeline at "now" if a note has already
        // been queued just ahead of it. Otherwise a resumed note can overtake
        // that queued note and the displayed string no longer matches audio.
        if (nextNoteTime.current <= audioContext.current.currentTime) {
            currentBeat.current = 0;
            nextNoteTime.current = audioContext.current.currentTime;
        }

        worker.postMessage({ type: START, interval: 25, ...state });
        setState({
            ...state,
            beat: 0,
        });
    };

    const stop = () => {
        // Keep the scheduled audio and exercise cursor intact for a clean resume.
        worker.postMessage({ type: STOP });
    };

    const reset = useCallback(() => {
        worker.postMessage({ type: STOP });
        setBeat(0);
        currentBeat.current = 0;
        exerciseStep.current = 0;
        nextNoteTime.current = audioContext.current?.currentTime ?? 0;
        setState(prevState => ({ ...prevState, beat: 0 }));
    }, []);

    const skip = useCallback(num => {
        setBeat(num);
        exerciseStep.current = num;
        currentBeat.current = 0;
        nextNoteTime.current = audioContext.current?.currentTime ?? 0;
        setState(prevState => ({ ...prevState, beat: 0 }));
    }, []);

    const onmessage = async e => {
        switch (e.data) {
            case TICK:
                runScheduler();
                break;
            default:
                console.log(`%ctype ${e.data} not recognised`, 'color: red;',);
        }
    };

    const onerror = async e => console.log('%cworker.onerror', 'color: red;', e);

    useEffect(() => {
        worker.onerror = onerror;
    }, [worker]);

    useEffect(() => {
        worker.onmessage = onmessage;
    }, [runScheduler]);

    useEffect(() => {
        if (running)
            start();
        else
            stop();
    }, [running]);

    useEffect(() => {
        worker.postMessage({ type: UPDATE, interval: 25 });
        setState(state => ({
            ...state,
            tempo,
        }));
    }, [tempo]);

    useEffect(() => {
        stop();
        setRunning(false);
        setBeat(beat => 0);
        currentBeat.current = 0;
        exerciseStep.current = 0;
        nextNoteTime.current = audioContext.current?.currentTime ?? 0;
        setBeatsPerMeasure(instrument.strings.length);
    }, [instrument.strings.length]);

    return { beat, running, stop, reset, skip };
};

export default useMetronome;
